/**
 * MarketingLayout — the front door to ARA.
 *
 * A quiet public shell that matches the Residence design language exactly.
 * Warm ivory canvas, hairline top-nav, Fraunces + Inter, no visual noise.
 * The transition from marketing → Residence should feel seamless.
 */
import { useState } from "react";
import { Link, NavLink } from "react-router-dom";

const NAV = [
  { to: "/how-it-works", label: "How it works" },
  { to: "/pricing",      label: "Pricing" },
  { to: "/about",        label: "About" },
  { to: "/contact",      label: "Contact" },
];

export function MarketingLayout({ children, testid = "marketing-page" }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="min-h-screen"
      style={{
        background: 'var(--ara-surface, #FAF9F6)',
        color: 'var(--ara-ink, #1F1F1F)',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
      data-testid={testid}
    >
      {/* ── Top-nav ─────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-40 backdrop-blur-sm"
        style={{
          background: 'rgba(250, 249, 246, 0.85)',
          borderBottom: '1px solid var(--ara-line, rgba(31, 31, 31, 0.08))',
        }}
      >
        <div className="mx-auto max-w-[1160px] px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2" data-testid="nav-brand">
            <span
              className="text-[13px] tracking-[0.24em] uppercase"
              style={{ color: 'var(--ara-ink, #1F1F1F)', fontWeight: 500 }}
            >
              ARA
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-x-8">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `text-[13px] transition-opacity hover:opacity-70`}
                style={({ isActive }) => ({
                  color: isActive ? 'var(--ara-ink, #1F1F1F)' : 'var(--ara-ink-muted, #4A4744)',
                  fontWeight: isActive ? 500 : 400,
                  borderBottom: isActive ? '1px solid var(--ara-ink, #1F1F1F)' : '1px solid transparent',
                  paddingBottom: '2px',
                })}
                data-testid={`nav-${item.to.replace('/', '')}`}
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/login"
              className="text-[13px] transition-opacity hover:opacity-70"
              style={{ color: 'var(--ara-ink-soft, #7A7772)', fontWeight: 400 }}
              data-testid="nav-signin"
            >
              Sign in
            </Link>
            <Link
              to="/join-beta"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 transition-opacity hover:opacity-80"
              style={{
                background: 'var(--ara-ink, #1F1F1F)',
                color: 'var(--ara-surface, #FAF9F6)',
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
              }}
              data-testid="nav-join-waitlist"
            >
              Join the waitlist →
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="md:hidden text-[13px]"
            style={{ color: 'var(--ara-ink-muted, #4A4744)' }}
            aria-expanded={open}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? 'Close' : 'Menu'}
          </button>
        </div>

        {open && (
          <div
            className="md:hidden px-6 py-6 space-y-4"
            style={{ borderTop: '1px solid var(--ara-line, rgba(31,31,31,0.08))' }}
          >
            {NAV.map(item => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="block text-[15px]"
                style={{ color: 'var(--ara-ink, #1F1F1F)' }}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/login" onClick={() => setOpen(false)} className="block text-[15px]" style={{ color: 'var(--ara-ink-muted, #4A4744)' }}>
              Sign in
            </Link>
            <Link to="/join-beta" onClick={() => setOpen(false)} className="block text-[15px]" style={{ color: 'var(--ara-ink, #1F1F1F)', fontWeight: 500 }}>
              Join the waitlist →
            </Link>
          </div>
        )}
      </header>

      {/* ── Main content ────────────────────────────────────────── */}
      <main className="mx-auto max-w-[1160px] px-6 sm:px-10">
        {children}
      </main>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer
        className="mt-32"
        style={{ borderTop: '1px solid var(--ara-line, rgba(31,31,31,0.08))' }}
      >
        <div className="mx-auto max-w-[1160px] px-6 sm:px-10 py-14 grid grid-cols-1 sm:grid-cols-4 gap-10">
          <div className="sm:col-span-2 max-w-sm">
            <p className="text-[11px] tracking-[0.24em] uppercase" style={{ color: 'var(--ara-ink-soft, #7A7772)' }}>ARA</p>
            <p
              className="mt-1 text-[19px] leading-tight"
              style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", fontWeight: 400 }}
            >
              Home Operating System
            </p>
            <p className="mt-4 text-[13.5px] leading-[1.55]" style={{ color: 'var(--ara-ink-muted, #4A4744)' }}>
              Built quietly, from Aotearoa New Zealand, for households everywhere.
            </p>
          </div>
          <div>
            <p className="text-[10.5px] tracking-[0.18em] uppercase mb-3" style={{ color: 'var(--ara-ink-soft, #7A7772)', fontWeight: 500 }}>
              Product
            </p>
            <ul className="space-y-2 text-[13px]" style={{ color: 'var(--ara-ink-muted, #4A4744)' }}>
              <li><Link to="/how-it-works">How it works</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
              <li><Link to="/join-beta">Join beta</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-[10.5px] tracking-[0.18em] uppercase mb-3" style={{ color: 'var(--ara-ink-soft, #7A7772)', fontWeight: 500 }}>
              Trust
            </p>
            <ul className="space-y-2 text-[13px]" style={{ color: 'var(--ara-ink-muted, #4A4744)' }}>
              <li><Link to="/about">About Ara</Link></li>
              <li><Link to="/privacy">Privacy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/security">Security</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li><Link to="/login">Sign in</Link></li>
            </ul>
          </div>
        </div>
        <div
          className="mx-auto max-w-[1160px] px-6 sm:px-10 py-6 flex items-center justify-between text-[11.5px] flex-wrap gap-3"
          style={{
            borderTop: '1px solid var(--ara-line-soft, rgba(31,31,31,0.06))',
            color: 'var(--ara-ink-soft, #7A7772)',
          }}
        >
          <p>© {new Date().getFullYear()} ARA · A quieter household</p>
          <p>Built quietly in Aotearoa New Zealand</p>
        </div>
      </footer>
    </div>
  );
}

/* ── Shared primitives (marketing only) ─────────────────────────── */

export function Eyebrow({ children, ...rest }) {
  return (
    <p
      className="text-[11px] tracking-[0.24em] uppercase"
      style={{ color: 'var(--ara-ink-soft, #7A7772)', fontWeight: 500 }}
      {...rest}
    >
      {children}
    </p>
  );
}

export function Display({ children, className = "", ...rest }) {
  return (
    <h1
      className={`text-[38px] sm:text-[54px] lg:text-[68px] leading-[1.02] tracking-[-0.02em] ${className}`}
      style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink, #1F1F1F)', fontWeight: 400 }}
      {...rest}
    >
      {children}
    </h1>
  );
}

export function SectionTitle({ children, className = "", ...rest }) {
  return (
    <h2
      className={`text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.08] tracking-[-0.015em] ${className}`}
      style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink, #1F1F1F)', fontWeight: 400 }}
      {...rest}
    >
      {children}
    </h2>
  );
}

export function FocalSentence({ children, className = "", ...rest }) {
  return (
    <p
      className={`italic text-[19px] sm:text-[22px] leading-[1.4] ${className}`}
      style={{ fontFamily: "var(--ara-font-display, 'Fraunces'), 'Times New Roman', serif", color: 'var(--ara-ink, #1F1F1F)', fontWeight: 400 }}
      {...rest}
    >
      {children}
    </p>
  );
}

export function Body({ children, className = "", ...rest }) {
  return (
    <p
      className={`text-[15.5px] leading-[1.6] ${className}`}
      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted, #4A4744)', fontWeight: 400 }}
      {...rest}
    >
      {children}
    </p>
  );
}

export function PrimaryCTA({ to, children, testid, onClick, type }) {
  const cls = "inline-flex items-center gap-2 rounded-full px-6 py-3 transition-opacity hover:opacity-80";
  const style = {
    background: 'var(--ara-ink, #1F1F1F)',
    color: 'var(--ara-surface, #FAF9F6)',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  };
  if (to) return <Link to={to} className={cls} style={style} data-testid={testid}>{children}</Link>;
  return <button type={type || "button"} onClick={onClick} className={cls} style={style} data-testid={testid}>{children}</button>;
}

export function SecondaryLink({ to, children, testid, onClick }) {
  const cls = "text-[13px] transition-opacity hover:opacity-70";
  const style = { color: 'var(--ara-ink, #1F1F1F)', borderBottom: '1px solid var(--ara-ink, #1F1F1F)', paddingBottom: '2px' };
  if (to) return <Link to={to} className={cls} style={style} data-testid={testid}>{children}</Link>;
  return <button type="button" onClick={onClick} className={cls} style={style} data-testid={testid}>{children}</button>;
}

export function Section({ children, className = "", testid, ...rest }) {
  return (
    <section className={`py-20 sm:py-28 ${className}`} data-testid={testid} {...rest}>
      {children}
    </section>
  );
}

export function Hairline({ className = "" }) {
  return <hr className={`border-0 h-px ${className}`} style={{ background: 'var(--ara-line, rgba(31,31,31,0.08))' }} />;
}
